/**
 * Things used by both FortinetDashboardComponent and FortinetFleetComponent.
 *
 *   - the document types both pages use, and the verdict thresholds
 *   - formatting helpers
 *   - issue helpers (sorting, acknowledgement notes, where to find the
 *     data behind an issue)
 *   - ChartKit: my glue around ECharts (theme colours, min/max bands,
 *     linked zoom, state kept between refreshes)
 *   - polling that pauses while the browser tab is hidden, or when I ask
 *   - TableSort, the column sorting of every table, and the CSV export
 *   - the event timeline types, and the acknowledgement note editor
 *   - describeApiError, one readable line from an error of the API
 *   - Anonymizer, for the "Share summary" export
 *
 * There is no component in here, so both component files can import it
 * without importing each other.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, InjectionToken, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, fromEvent, merge, timer } from 'rxjs';
import { filter } from 'rxjs/operators';

/* ECharts, tree-shaken: I import the core and register exactly what the two
   pages draw. If I add a chart type or a component somewhere, I have to add
   it here too, otherwise ECharts throws at setOption. That's the price of a
   small bundle. */
import * as echarts from 'echarts/core';
import {
  BarChart, LineChart, PieChart, SankeyChart, ScatterChart,
} from 'echarts/charts';
import {
  AxisPointerComponent, DataZoomComponent, DatasetComponent, GridComponent,
  LegendComponent, MarkLineComponent, TitleComponent, ToolboxComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsOption, SeriesOption } from 'echarts';

echarts.use([
  BarChart, LineChart, PieChart, SankeyChart, ScatterChart,
  AxisPointerComponent, DataZoomComponent, DatasetComponent, GridComponent,
  LegendComponent, MarkLineComponent, TitleComponent, ToolboxComponent,
  TooltipComponent, CanvasRenderer,
]);

/* ============================================================================
 * Shared types
 * ========================================================================== */

/** The values of one line, in the time base of its chart. */
export type Values = (number | null)[];
export type Sev = 'danger' | 'warning' | 'info';
export type Formatter = (value: number | null | undefined) => string;

/** Min / max band around a bucketed line. */
export interface Band {
  min?: Values;
  max?: Values;
}

export interface HistoryRange {
  id: string;
  label: string;
}

export type BadgeClass = 'label-success' | 'label-warning' | 'label-danger' | 'label-info';

export interface Badge {
  cls: BadgeClass;
  text: string;
}

/** A device I can pick (meta.devices of either document). */
export interface DeviceRef {
  serial: string;
  label: string;
  /** main group first (config.yaml `groups:`); absent from older backends */
  groups?: string[];
  /** the single group sent by older backends */
  group?: string | null;
}

/* ==========================================================================
 * Groups of devices
 *
 * A device can be in several groups, and a "/" in a group makes a path:
 * "France/Paris" is the group Paris inside the group France. A device in
 * "France/Paris" also counts in "France". The FIRST group of the list is the
 * main group: the NetScope menu shows the device under it only.
 * The same rules are written in Python in netscope/core/fortinet/groups.py.
 * ========================================================================== */

export const GROUP_SEPARATOR = '/';

/** The groups of a device, main group first, whatever backend sent it
 *  (older backends send one `group` text instead of the `groups` list). */
export function groupsOfDevice(device: { groups?: string[] | null; group?: string | null }): string[] {
  if (Array.isArray(device.groups)) {
    return device.groups;
  }
  if (device.group) {
    return [device.group];
  }
  return [];
}

/** The main group of a device, or '' when it has no group. */
export function mainGroupOf(groups: string[]): string {
  if (groups.length === 0) {
    return '';
  }
  return groups[0];
}

/** The group itself and every group above it, from the top:
 *  "Europe/France/Paris" gives ["Europe", "Europe/France", "Europe/France/Paris"]. */
export function groupAndParents(group: string): string[] {
  const parts = group.split(GROUP_SEPARATOR);
  const result: string[] = [];
  for (let depth = 1; depth <= parts.length; depth++) {
    result.push(parts.slice(0, depth).join(GROUP_SEPARATOR));
  }
  return result;
}

/** Every group a device counts in (its groups and the groups above them), each once. */
export function groupsWithParents(groups: string[]): string[] {
  const result: string[] = [];
  for (const group of groups) {
    for (const groupOrParent of groupAndParents(group)) {
      if (!result.includes(groupOrParent)) {
        result.push(groupOrParent);
      }
    }
  }
  return result;
}

/** How deep a group is: 0 for "France", 1 for "France/Paris". */
export function groupDepth(group: string): number {
  return group.split(GROUP_SEPARATOR).length - 1;
}

/** The last level of a group: "Paris" for "France/Paris". */
export function groupLeafName(group: string): string {
  const parts = group.split(GROUP_SEPARATOR);
  return parts[parts.length - 1];
}

/** A group written for people: "France › Paris". */
export function groupPathText(group: string): string {
  return group.split(GROUP_SEPARATOR).join(' › ');
}

/** Is a device in this group, or in a group below it?
 *  filter: null for every device, '' for the devices without any group. */
export function deviceIsInGroup(groups: string[], filter: string | null): boolean {
  if (filter === null) {
    return true;
  }
  if (filter === '') {
    return groups.length === 0;
  }
  return groupsWithParents(groups).includes(filter);
}

/** Sorts groups so that each group comes right before the groups inside it,
 *  and the devices without a group ('') come last:
 *  France, France/Lyon, France/Paris, Production, ''. */
export function compareGroupsAsTree(first: string, second: string): number {
  if (first === second) return 0;
  if (first === '') return 1;
  if (second === '') return -1;
  const firstParts = first.toLowerCase().split(GROUP_SEPARATOR);
  const secondParts = second.toLowerCase().split(GROUP_SEPARATOR);
  // compare level by level; the first level that differs decides
  const commonDepth = Math.min(firstParts.length, secondParts.length);
  for (let depth = 0; depth < commonDepth; depth++) {
    const comparison = firstParts[depth].localeCompare(secondParts[depth]);
    if (comparison !== 0) {
      return comparison;
    }
  }
  // one is the parent of the other: the parent ("France") comes first
  return firstParts.length - secondParts.length;
}

/** The acknowledgement fields an issue can carry (dashboard.apply_issue_acks).
 *  All optional: an older dashboard.py sends none of them. */
export interface AckFields {
  /** stays the same across polls, the acknowledgements are keyed on it */
  id?: string;
  /** epoch ms, set on acknowledged issues */
  acked_at?: number | null;
  /** the severity when it was acknowledged. On an OPEN issue it means it
   *  was acknowledged but got worse since */
  acked_sev?: Sev | null;
  /** acknowledged issues only: still detected right now */
  present?: boolean;
  /** epoch ms when the problem started (from the event timeline) or when
   *  the dashboard first detected it. Missing on older backends */
  first_seen?: number | null;
  /** first_seen is only a lower bound: it was already there when the
   *  window starts */
  first_seen_bound?: boolean;
  /** why it was acknowledged, and by whom (both optional) */
  ack_note?: string;
  ack_by?: string;
  ack_note_at?: number;
}

/* ============================================================================
 * Verdict thresholds (meta.thresholds, same as dashboard.THRESHOLDS)
 * ========================================================================== */

export interface Level {
  warning: number;
  danger?: number;
}

export interface Thresholds {
  cpu: Level;
  memory: Level;
  util: Level;
  /** days left: lower is worse */
  license_days: Level;
  sdwan_loss: Level;
  sdwan_latency: Level;
  sdwan_jitter: Level;
  /** days left on a certificate: lower is worse */
  cert_days: Level;
  /** seconds between the device clock and the collector's clock */
  clock_skew: Level;
}

/** Used when the backend sends no thresholds (older dashboard.py). */
export const DEFAULT_THRESHOLDS: Thresholds = {
  cpu: { warning: 80, danger: 90 },
  memory: { warning: 75, danger: 88 },
  util: { warning: 80, danger: 95 },
  license_days: { warning: 90, danger: 30 },
  sdwan_loss: { warning: 2 },
  sdwan_latency: { warning: 150, danger: 300 },
  sdwan_jitter: { warning: 30, danger: 60 },
  cert_days: { warning: 30, danger: 7 },
  clock_skew: { warning: 30, danger: 300 },
};

export function thresholdsOf(raw?: Partial<Thresholds> | null): Thresholds {
  const result = { ...DEFAULT_THRESHOLDS };
  const keys = Object.keys(result) as (keyof Thresholds)[];
  for (const key of keys) {
    const level = raw?.[key];
    // I only take a level from the backend when it looks valid
    if (level && typeof level.warning === 'number') {
      result[key] = level;
    }
  }
  return result;
}

/** 'danger' / 'warning' / null for a value where higher is worse. */
export function levelOf(value: number | null | undefined, level: Level): Sev | null {
  if (value == null) return null;
  if (level.danger != null && value >= level.danger) return 'danger';
  if (value >= level.warning) return 'warning';
  return null;
}

/** Same thing, for days left, where lower is worse. */
export function daysLevelOf(days: number | null | undefined, level: Level): Sev | null {
  if (days == null) return null;
  if (days < 0 || (level.danger != null && days < level.danger)) return 'danger';
  if (days < level.warning) return 'warning';
  return null;
}

/** The table cell class for a value judged by levelOf. */
export function levelCls(value: number | null | undefined, level: Level): string {
  if (value == null) return 'dim';
  const verdict = levelOf(value, level);
  if (verdict === 'danger') return 'err-warn';
  if (verdict === 'warning') return 'util-warn';
  return '';
}

/** A value between 0 and 100. CPU and memory are only judged then (the
 *  shape FortiOS reports changes with the version). */
export function isPct(value: number | null | undefined): value is number {
  return value != null && value >= 0 && value <= 100;
}

/* ============================================================================
 * Formatting
 * ========================================================================== */

export function fmtNum(value: number | null | undefined): string {
  if (value == null) return '-';
  if (Math.abs(value) >= 1e6) return (value / 1e6).toFixed(1) + 'M';
  if (Math.abs(value) >= 1e3) return (value / 1e3).toFixed(1) + 'k';
  return String(Math.round(value));
}

export function fmtBytes(value: number | null | undefined): string {
  if (value == null) return '-';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let unitIndex = 0;
  // divide by 1024 until the number is small enough (or I run out of units)
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  // one decimal for small numbers ("2.5 MiB"), except for plain bytes
  const decimals = value < 10 && unitIndex > 0 ? 1 : 0;
  return value.toFixed(decimals) + ' ' + units[unitIndex];
}

export function fmtBps(value: number | null | undefined): string {
  if (value == null) return '-';
  const units = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s'];
  let unitIndex = 0;
  // same idea as fmtBytes, but network speeds go by 1000
  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex++;
  }
  const decimals = value < 10 && unitIndex > 0 ? 1 : 0;
  return value.toFixed(decimals) + ' ' + units[unitIndex];
}

/** Percentages (CPU, memory): one decimal below 10, so a 2.5 % load doesn't
 *  show as 3 %. */
export function fmtPct(value: number | null | undefined): string {
  if (value == null) return '-';
  if (Math.abs(value) < 10 && value !== 0) {
    return value.toFixed(1) + ' %';
  }
  return value.toFixed(0) + ' %';
}

/** A percentage when the value looks like one, a plain number otherwise. */
export function fmtPctOrNum(value: number | null | undefined): string {
  if (isPct(value)) return fmtPct(value);
  return fmtNum(value);
}

/** Small per-second rates (errors/s): fmtNum would round 0.003 to 0. */
export function fmtRate(value: number | null | undefined): string {
  if (value == null) return '-';
  if (value === 0 || Math.abs(value) >= 10) return fmtNum(value);
  return value.toPrecision(2) + '/s';
}

/** "42 s", "7 min", "3 h", "2 d" */
export function fmtAge(seconds: number | null | undefined): string {
  if (seconds == null) return 'never';
  if (seconds < 90) return `${Math.round(seconds)} s`;
  // 5400 s = 1.5 hour
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  // 172800 s = 2 days
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

/** Two digits, with a leading zero ("07"). */
function twoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

/** "2026-09-17 10:42" in local time, without the seconds. */
export function fmtStamp(ms: number | null | undefined): string {
  if (ms == null) return '-';
  const date = new Date(ms);
  const day = `${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`;
  const time = `${twoDigits(date.getHours())}:${twoDigits(date.getMinutes())}`;
  return `${day} ${time}`;
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};

/** HTML-escape. I still need it inside the ECharts tooltip formatters,
 *  because they take raw HTML strings (Angular's escaping doesn't reach
 *  them). The single quote too, so a text from the data stays harmless
 *  even inside an attribute written with '...'. */
export function esc(text: unknown): string {
  return String(text).replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);
}

/* ============================================================================
 * Issues
 * ========================================================================== */

export const SEV_RANK: Record<Sev, number> = { danger: 0, warning: 1, info: 2 };
export const SEV_WORD: Record<Sev, string> = {
  danger: 'critical', warning: 'warning', info: 'note',
};

export function issueBadge(sev: Sev): Badge {
  if (sev === 'danger') return { cls: 'label-danger', text: 'fix' };
  if (sev === 'warning') return { cls: 'label-warning', text: 'check' };
  return { cls: 'label-info', text: 'note' };
}

/** Badge class of the most severe count that is not zero. */
export function worstSevClass(counts: Record<Sev, number>): BadgeClass {
  if (counts.danger) return 'label-danger';
  if (counts.warning) return 'label-warning';
  return 'label-info';
}

/** Order of the acknowledged list: the ones detected right now first, then
 *  by severity, then the most recently acknowledged. */
export function sortAckedIssues<T extends AckFields & { sev: Sev }>(list: T[]): T[] {
  const sorted = list.slice();
  sorted.sort((first, second) => {
    // "present === false" means not detected anymore: those go last
    const byPresence = Number(first.present === false) - Number(second.present === false);
    if (byPresence !== 0) return byPresence;
    const bySeverity = SEV_RANK[first.sev] - SEV_RANK[second.sev];
    if (bySeverity !== 0) return bySeverity;
    return (second.acked_at ?? 0) - (first.acked_at ?? 0);
  });
  return sorted;
}

/** Order of the open list: by severity, otherwise unchanged. */
export function sortOpenIssues<T extends { sev: Sev }>(list: T[]): T[] {
  const sorted = list.slice();
  sorted.sort((first, second) => SEV_RANK[first.sev] - SEV_RANK[second.sev]);
  return sorted;
}

/** One line under an issue explaining its acknowledgement, or ''. */
export function ackNote(issue: AckFields, open: boolean): string {
  if (open) {
    if (issue.acked_sev) {
      return `Acknowledged as ${SEV_WORD[issue.acked_sev]}, but it got worse since.`;
    }
    return '';
  }
  let base = 'Acknowledged';
  if (issue.acked_at) {
    base = `Acknowledged ${fmtStamp(issue.acked_at)}`;
  }
  if (issue.present === false) {
    return `${base}; not detected right now.`;
  }
  return `${base}.`;
}

/** "first detected 3 h ago", "for at least 24 h (...)", or '' when the
 *  backend doesn't say. */
export function firstSeenText(issue: AckFields, now = Date.now()): string {
  if (issue.first_seen == null) return '';
  const ageSeconds = Math.max(0, (now - issue.first_seen) / 1000);
  const age = fmtAge(ageSeconds);
  if (issue.first_seen_bound) {
    return `for at least ${age} (since before the window)`;
  }
  return `first detected ${age} ago`;
}

/** Title (tooltip) of firstSeenText: the date itself. */
export function firstSeenTitle(issue: AckFields): string {
  if (issue.first_seen == null) return '';
  const prefix = issue.first_seen_bound ? 'Already there at ' : 'Since ';
  return prefix + fmtStamp(issue.first_seen);
}

/** '"Spare port" (alice)', or '' when there's no note. */
export function ackNoteLine(issue: AckFields): string {
  const note = (issue.ack_note ?? '').trim();
  const by = (issue.ack_by ?? '').trim();
  if (!note && !by) return '';
  let line = note ? `"${note}"` : 'Note';
  if (by) {
    line += ` (${by})`;
  }
  return line;
}

/** True when an issue id belongs to one of `keys`: either the key itself
 *  ("cpu") or the key followed by ":<subject>" ("iface-down:port3"). */
export function issueMatches(id: string | undefined, keys: readonly string[]): boolean {
  if (!id) return false;
  for (const key of keys) {
    if (id === key || id.startsWith(key + ':')) {
      return true;
    }
  }
  return false;
}

/** The tabs of the device page. */
export type DeviceTab = 'overview' | 'traffic' | 'map' | 'interfaces' | 'policies' | 'vpn'
  | 'system';

/** Where the data behind an issue is on the device page: a tab and the
 *  elements to highlight, most precise first (a table row, then the card
 *  it's in). The elements are marked with data-ev="<anchor>". */
export interface Evidence {
  tab: DeviceTab;
  anchors: string[];
}

export function evidenceOf(id: string | undefined | null): Evidence | null {
  if (!id) return null;
  // An id looks like "key:subject", e.g. "iface-down:port3"
  const colonIndex = id.indexOf(':');
  let key = id;
  let subject = '';
  if (colonIndex >= 0) {
    key = id.slice(0, colonIndex);
    subject = id.slice(colonIndex + 1);
  }
  switch (key) {
    case 'iface-down': case 'iface-duplex': case 'iface-util': case 'iface-flap':
      return { tab: 'traffic', anchors: ['iface:' + subject, 'card:interfaces'] };
    case 'iface-errors':
      return { tab: 'traffic', anchors: ['iface:' + subject, 'card:iface-errors', 'card:interfaces'] };
    case 'counter-reset':
      return { tab: 'traffic', anchors: ['card:iface-chart'] };
    case 'cpu': case 'memory': case 'conserve': case 'conserve-recent':
      return { tab: 'system', anchors: ['card:cpumem'] };
    case 'ntp': case 'clock-skew':
      return { tab: 'system', anchors: ['card:time'] };
    case 'cert-expired': case 'cert-expiring':
      return { tab: 'system', anchors: ['cert:' + subject, 'card:certs'] };
    case 'bgp-down': case 'bgp-flap':
      return { tab: 'vpn', anchors: ['bgp:' + subject, 'card:routing'] };
    case 'ospf-down': case 'ospf-flap':
      return { tab: 'vpn', anchors: ['ospf:' + subject, 'card:routing'] };
    case 'policy-unused':
      return { tab: 'policies', anchors: ['card:less-used'] };
    case 'policy-oversize':
      return { tab: 'policies', anchors: ['card:top-used'] };
    case 'license-expired': case 'license-expiring':
      return { tab: 'overview', anchors: ['license:' + subject, 'card:licenses'] };
    case 'firmware-upgrade':
      return { tab: 'overview', anchors: ['card:licenses'] };
    case 'sensor':
      return { tab: 'system', anchors: ['sensor:' + subject, 'card:sensors'] };
    case 'fortiguard':
      return { tab: 'system', anchors: ['card:sensors'] };
    case 'ha-single':
      return { tab: 'system', anchors: ['card:ha'] };
    case 'ipsec-down': case 'ipsec-flap':
      return { tab: 'vpn', anchors: ['tunnel:' + subject, 'card:tunnels'] };
    case 'sdwan-down': case 'sdwan-loss': case 'sdwan-flap':
      return { tab: 'vpn', anchors: ['sdwan:' + subject, 'card:sdwan-members'] };
    // the row of that endpoint in the Endpoint polls card (the subject is
    // the file suffix, like "certificates"); the card itself if the row is
    // filtered out
    case 'poll-failing': case 'poll-absent':
      return { tab: 'system', anchors: ['poll:' + subject, 'card:polls'] };
    default:
      return null;
  }
}

/* ============================================================================
 * Event timeline (document `events`, dashboard.build_events)
 * ========================================================================== */

export type EventKind = 'link' | 'ipsec' | 'sdwan' | 'bgp' | 'ospf' | 'conserve'
  | 'reboot' | 'firmware' | 'ha' | 'admin';
/** 'ok' is a recovery (a link back up, a peer established again) */
export type EventSev = Sev | 'ok';

export interface FortiEvent {
  /** epoch ms of the first poll showing the new state, or the exact time
   *  (logins, reboots) */
  t: number;
  /** epoch ms of the last poll still showing the old state, null when `t`
   *  is exact */
  after: number | null;
  kind: EventKind;
  sev: EventSev;
  subject: string;
  title: string;
  /** issue-style id pointing to the data (see evidenceOf), or null */
  ref: string | null;
}

export const EVENT_KIND_LABEL: Record<EventKind, string> = {
  link: 'Links', ipsec: 'IPsec', sdwan: 'SD-WAN', bgp: 'BGP', ospf: 'OSPF',
  conserve: 'Conserve mode', reboot: 'Reboots', firmware: 'Firmware',
  ha: 'HA', admin: 'Admins',
};

/** Badge of an event: what happened, not what to do (the issue badges say
 *  "fix" / "check"). */
export function eventBadge(sev: EventSev): Badge {
  if (sev === 'ok') return { cls: 'label-success', text: 'ok' };
  if (sev === 'danger') return { cls: 'label-danger', text: 'critical' };
  if (sev === 'warning') return { cls: 'label-warning', text: 'warning' };
  return { cls: 'label-info', text: 'info' };
}

/** Title of an event's time: the interval the change happened in. */
export function eventWhen(event: FortiEvent): string {
  if (event.after != null) {
    return `Between ${fmtStamp(event.after)} and ${fmtStamp(event.t)} (two consecutive polls)`;
  }
  return fmtStamp(event.t);
}

/** The kinds present in a list, in the order of EVENT_KIND_LABEL. */
export function eventKindsOf(list: FortiEvent[]): EventKind[] {
  const present = new Set<EventKind>();
  for (const event of list) {
    present.add(event.kind);
  }
  const allKinds = Object.keys(EVENT_KIND_LABEL) as EventKind[];
  const result: EventKind[] = [];
  for (const kind of allKinds) {
    if (present.has(kind)) {
      result.push(kind);
    }
  }
  return result;
}

/* ============================================================================
 * Acknowledgement notes
 * ========================================================================== */

/** The name written in an acknowledgement note ("by"), when the host app
 *  knows who is logged in. Optional: without it the note form asks, and
 *  remembers the answer in this browser.
 *
 *    providers: [{ provide: FORTINET_USER,
 *                  useFactory: () => () => inject(AuthService).userName }]
 */
export type UserSource = string | (() => string | null | undefined);
export const FORTINET_USER = new InjectionToken<UserSource>('FORTINET_USER');

const BY_STORAGE_KEY = 'fgAckBy';

/** State of the small "note" form of an acknowledged issue. Only one is
 *  open at a time, keyed by the component's own issue key. */
export class AckNoteEditor {
  key: string | null = null;
  note = '';
  by = '';
  saving = false;
  error = '';

  constructor(private user: UserSource | null) {}

  /** True when the host app gives the name (then the field is read-only). */
  get byFixed(): boolean {
    return !!this.userName();
  }

  open(key: string, issue: AckFields): void {
    this.key = key;
    this.note = issue.ack_note ?? '';
    // the name already on the note, otherwise the logged-in user,
    // otherwise the last name typed in this browser
    this.by = issue.ack_by || this.userName() || this.remembered();
    this.error = '';
    this.saving = false;
  }

  close(): void {
    this.key = null;
    this.error = '';
    this.saving = false;
  }

  /** The values to send. The name is remembered for the next note. */
  payload(): { note: string; by: string } {
    const by = (this.userName() || this.by).trim();
    try {
      if (by && !this.byFixed) {
        localStorage.setItem(BY_STORAGE_KEY, by);
      }
    } catch {
      // storage not available: nothing to remember, not a big deal
    }
    return { note: this.note.trim(), by };
  }

  private userName(): string {
    let name: string | null | undefined;
    if (typeof this.user === 'function') {
      name = this.user();
    } else {
      name = this.user;
    }
    return (name ?? '').trim();
  }

  private remembered(): string {
    try {
      return localStorage.getItem(BY_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  }
}

/* ============================================================================
 * Polling
 * ========================================================================== */

/** Emits every time the page should refresh: once at the start, then every
 *  refreshMs while the page is visible, plus once when the page becomes
 *  visible again after missing at least one refresh. Nothing is fetched for
 *  a tab left in the background, or while `paused()` says so (the very
 *  first load still happens). */
export function pollTriggers(refreshMs: number,
                             lastLoadedAt: () => number,
                             paused: () => boolean = () => false): Observable<unknown> {
  if (refreshMs <= 0) {
    return timer(0);
  }

  // tickIndex 0 is the first load, which always goes through
  const ticks$ = timer(0, refreshMs).pipe(
    filter((tickIndex) => {
      if (tickIndex === 0) return true;
      return !document.hidden && !paused();
    }),
  );

  const backToTab$ = fromEvent(document, 'visibilitychange').pipe(
    filter(() => {
      if (document.hidden || paused()) return false;
      // only if I missed at least one refresh while the tab was hidden
      return Date.now() - lastLoadedAt() >= refreshMs;
    }),
  );

  return merge(ticks$, backToTab$);
}

/* ============================================================================
 * Sortable tables
 * ========================================================================== */

/** A value a table can be sorted on. null, undefined and '' mean "nothing
 *  to sort on": those rows always go to the bottom. */
export type SortValue = number | string | boolean | null | undefined;

/** Gives the value of a row for a column (the column is the sort key). */
export type SortValueOf<T> = (row: T, key: string) => SortValue;

function isMissing(value: SortValue): boolean {
  return value === null || value === undefined || value === '';
}

/** Compares two values that are both there (not missing). */
export function compareSortValues(first: SortValue, second: SortValue): number {
  if (typeof first === 'string' && typeof second === 'string') {
    // numeric: true compares the digits as numbers, so "port2" comes
    // before "port10" and "10.0.0.9" before "10.0.0.10"
    return first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });
  }
  // numbers and booleans (false is 0, true is 1)
  const firstNumber = Number(first);
  const secondNumber = Number(second);
  if (firstNumber < secondNumber) return -1;
  if (firstNumber > secondNumber) return 1;
  return 0;
}

/**
 * The sort of one table: which column, and in which direction.
 *
 * Every sortable table of the pages has one. A click on a column title
 * calls toggle(key):
 *   - first click on a column: its most useful direction. Text starts
 *     A to Z; the keys listed in descendingFirst start with the biggest
 *     value (throughput, errors, ...).
 *   - second click: the other direction.
 *   - third click: back to the default order of the table.
 *
 * The template shows the state with mark(key) (the little arrow) and
 * ariaSort(key) (for screen readers), and apply() gives the sorted rows.
 */
/** The keyboard of the two tabs of an issues card (Open, Acknowledged),
 *  as the ARIA Authoring Practices tabs pattern asks: the left and right
 *  arrows go to the other tab (they wrap around: with two tabs, both go to
 *  the other one), Home goes to the first, End to the last.
 *  Returns the tab to select, or null for any other key. */
export function issuesTabFromKey(key: string, current: 'open' | 'acked'): 'open' | 'acked' | null {
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    if (current === 'open') {
      return 'acked';
    }
    return 'open';
  }
  if (key === 'Home') {
    return 'open';
  }
  if (key === 'End') {
    return 'acked';
  }
  return null;
}

export class TableSort {
  /** the column the table is sorted on, null for the default order */
  key: string | null;
  ascending: boolean;
  /** the keys whose first click sorts the biggest value first */
  descendingFirst: string[];

  private readonly defaultKey: string | null;
  private readonly defaultAscending: boolean;

  /**
   * defaultKey:       the column sorted when the page opens, null to keep
   *                   the order the rows arrive in
   * defaultAscending: the direction of that default column
   * descendingFirst:  see above
   */
  constructor(defaultKey: string | null = null, defaultAscending = true,
              descendingFirst: string[] = []) {
    this.defaultKey = defaultKey;
    this.defaultAscending = defaultAscending;
    this.descendingFirst = descendingFirst;
    this.key = defaultKey;
    this.ascending = defaultAscending;
  }

  /** A click on the title of a column. */
  toggle(key: string): void {
    const startsAscending = !this.descendingFirst.includes(key);

    if (this.key !== key) {
      // first click on this column
      this.key = key;
      this.ascending = startsAscending;
      return;
    }

    if (this.ascending === startsAscending) {
      // second click: the other direction
      this.ascending = !startsAscending;
      return;
    }

    // third click: back to the default order
    this.key = this.defaultKey;
    this.ascending = this.defaultAscending;
  }

  /** The arrow next to a column title ('' when the table isn't sorted on it). */
  mark(key: string): string {
    if (this.key !== key) return '';
    return this.ascending ? '▲' : '▼';
  }

  /** The aria-sort attribute of a column title. null (no attribute) on the
   *  columns the table is not sorted on: the ARIA Authoring Practices
   *  (sortable table) put aria-sort on ONE column title only, otherwise a
   *  screen reader says "not sorted" on every other column. */
  ariaSort(key: string): string | null {
    if (this.key !== key) return null;
    return this.ascending ? 'ascending' : 'descending';
  }

  /** A sorted copy of the rows. The rows I get are never changed.
   *  Two rows with the same value keep the order they had (JavaScript's
   *  sort is stable), so the default order breaks the ties. */
  apply<T>(rows: readonly T[], valueOf: SortValueOf<T>): T[] {
    const sorted = rows.slice();
    const key = this.key;
    if (key === null) {
      return sorted;
    }

    const direction = this.ascending ? 1 : -1;
    sorted.sort((first, second) => {
      const firstValue = valueOf(first, key);
      const secondValue = valueOf(second, key);
      const firstMissing = isMissing(firstValue);
      const secondMissing = isMissing(secondValue);

      // the missing values always go to the bottom, in both directions,
      // so that's checked before the direction is applied
      if (firstMissing && secondMissing) return 0;
      if (firstMissing) return 1;
      if (secondMissing) return -1;

      return compareSortValues(firstValue, secondValue) * direction;
    });
    return sorted;
  }
}

/* ============================================================================
 * CSV export
 * ========================================================================== */

/** One CSV cell. Text that a spreadsheet would run as a formula (starting
 *  with = + - @ or a control character) gets a quote in front, so a policy
 *  or interface name can't inject one. Numbers are written as they are. */
function csvCell(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) {
    text = "'" + text;
  }
  // quotes, commas, semicolons or line breaks: the cell goes in quotes
  // and the quotes inside are doubled
  if (/[",;\n\r]/.test(text)) {
    return '"' + text.replace(/"/g, '""') + '"';
  }
  return text;
}

/** Downloads a table as a CSV file (UTF-8 with a BOM, so Excel shows the
 *  accents right). `name` gets the date and the .csv extension. */
export function downloadCsv(name: string, header: string[],
                            rows: unknown[][]): void {
  const lines: string[] = [];
  for (const row of [header, ...rows]) {
    const cells = row.map(csvCell);
    lines.push(cells.join(','));
  }
  // \ufeff is the BOM
  const content = '\ufeff' + lines.join('\r\n') + '\r\n';
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const stamp = fmtStamp(Date.now()).replace(/[: ]/g, '-');
  link.download = `${name}-${stamp}.csv`;
  link.click();
  // give the browser a second to start the download before freeing it
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

/* ============================================================================
 * API errors
 * ========================================================================== */

/** One readable line out of anything a request to the NetScope API threw
 *  (see netscope/web/fortinet_blueprint.py). The API explains its errors in
 *  the 'message' of a JSON body. */
export function describeApiError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error;
    if (body && typeof body === 'object' && 'message' in body) {
      return `HTTP ${error.status}: ${(body as { message: string }).message}`;
    }
    if (error.status === 401) {
      return 'the NetScope API refused the request (HTTP 401); your session '
        + 'may have expired, reload the page';
    }
    if (typeof body === 'string' && body.length) {
      return `HTTP ${error.status}: ${body}`;
    }
    if (error.status === 0) {
      return 'network error: is the NetScope API reachable?';
    }
    return `HTTP ${error.status} ${error.statusText}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/* ============================================================================
 * Charts
 * ========================================================================== */

/** Fallback series palette: the Highcharts light theme values. Used when
 *  the --highcharts-color-N CSS variables can't be read from the host. */
export const FALLBACK_PALETTE = [
  'rgb(0, 121, 173)',   // 0 blue
  'rgb(255, 185, 46)',  // 1 yellow
  'rgb(102, 209, 255)', // 2 light blue
  'rgb(0, 64, 133)',    // 3 dark blue
  'rgb(255, 142, 61)',  // 4 orange
  'rgb(155, 50, 200)',  // 5 purple
  'rgb(79, 97, 105)',   // 6 slate grey
  'rgb(224, 34, 0)',    // 7 red
  'rgb(0, 35, 71)',     // 8 navy
  'rgb(66, 129, 14)',   // 9 green
];
export const FONT = '"Clarity City","Avenir Next","Segoe UI",Arial,sans-serif';

/** id prefix of the helper series that draw a min/max band. The tooltip
 *  skips them and shows the range next to the line instead. */
export const BAND_ID_PREFIX = 'band:';
/** id of the zoom component of every time chart. The linked charts find
 *  each other's zoom with it. */
export const TIME_ZOOM_ID = 'time-zoom';
/** The plot area of every chart (see ChartKit.base). containLabel: the
 *  margins are measured from the axis labels, so wide labels like
 *  "350 Mbit/s" never get cut. */
const DEFAULT_GRID = { left: 12, right: 20, top: 30, bottom: 55, containLabel: true };
/** Top margin of a chart with an axis on the RIGHT (see
 *  ChartKit.gridUnderZoomTool). The zoom tool takes about the top 18 px of
 *  the right corner; at 44 px, the highest label of the right axis (drawn
 *  centred on the top of the plot) starts at about 37 px, clear of it. */
const ZOOM_TOOL_GRID_TOP = 44;

/** A gap bigger than this many times the usual sampling interval AROUND it
 *  counts as an outage: the line breaks instead of bridging the hole (see
 *  isOutage). */
const GAP_FACTOR = 2.5;
/** How many intervals on each side of a hole give "the usual interval
 *  around it" (see isOutage). */
const GAP_NEIGHBOURS = 10;

/** The median of some numbers (the upper one of the two middle values when
 *  there is an even count), or null when there are none. */
function medianOf(numbers: number[]): number | null {
  if (numbers.length === 0) {
    return null;
  }
  const sorted = numbers.slice();
  sorted.sort((firstNumber, secondNumber) => firstNumber - secondNumber);
  return sorted[Math.floor(sorted.length / 2)];
}

/**
 * True when the hole between two samples, deltas[index] (milliseconds), is
 * an outage: more than GAP_FACTOR times the usual interval on BOTH sides
 * of it.
 *
 * "Around it" matters. The interval is not the same everywhere in one
 * series: the polling interval may have changed, and on a long window the
 * old points are buckets (one every N polls) while the last two points are
 * raw. I used to compare every hole with the median of the WHOLE series:
 * when most points were close together, every point of the sparser part
 * looked like it came after an outage, got cut off from both neighbours,
 * and an isolated point is invisible (lines have no dots). That hid whole
 * days of the CPU & memory chart on the 7-day window.
 *
 * Why both sides, and the larger of the two: where the interval changes
 * (1 min before, 5 min after), the hole in between must not count as an
 * outage. And with an irregular rhythm (two polls a few seconds apart,
 * then a long wait), the median of a short window can land on the small
 * interval; taking the larger side avoids false breaks there.
 */
function isOutage(deltas: number[], index: number): boolean {
  // up to GAP_NEIGHBOURS intervals just before this one...
  const before: number[] = [];
  for (let other = Math.max(0, index - GAP_NEIGHBOURS); other < index; other++) {
    before.push(deltas[other]);
  }
  // ...and up to GAP_NEIGHBOURS just after it
  const after: number[] = [];
  const last = Math.min(deltas.length - 1, index + GAP_NEIGHBOURS);
  for (let other = index + 1; other <= last; other++) {
    after.push(deltas[other]);
  }

  const usualBefore = medianOf(before);
  const usualAfter = medianOf(after);
  // with no neighbour at all (a series of two points) nothing can be compared
  if (usualBefore === null && usualAfter === null) {
    return false;
  }

  // the larger usual interval of the two sides (a missing side doesn't count)
  const usual = Math.max(usualBefore ?? 0, usualAfter ?? 0);
  return usual > 0 && deltas[index] > usual * GAP_FACTOR;
}

interface SavedZoom {
  id?: string;
  start?: number;
  end?: number;
  startValue?: number;
  endValue?: number;
}

interface PrevOption {
  legend?: { selected?: Record<string, boolean> }[];
  dataZoom?: SavedZoom[];
}

type AnyObj = { [key: string]: unknown };

// counter so every ChartKit gets its own connect group name
let kitSequence = 0;

/** Options of ChartKit.thresholdMarks, for a chart that shows several sets
 *  of threshold lines (CPU and memory on one chart). */
export interface ThresholdMarkOptions {
  /** put in front of the label: "CPU warning 80 %" */
  name?: string;
  /** end of the line where the label sits (default 'end'). Each set gets
   *  its own end, so labels of close values don't pile up */
  at?: 'start' | 'end';
  /** dotted instead of dashed, to tell a second set apart */
  dotted?: boolean;
  /** label under its line instead of above it, so it doesn't collide with
   *  a line of the other set just above (memory 88 % under CPU 90 %) */
  below?: boolean;
}

/* ----------------------------------------------------------------------------
 * Text summary of a chart, for screen readers
 *
 * A chart is drawn on a canvas, which a screen reader can't read. ECharts can
 * write a description by itself (its "aria" option), but it reads the raw
 * data in order, at most 10 points per series: on a time chart, that is the
 * 10 OLDEST points of the window, as raw timestamps, with the min / max bands
 * counted as extra series and no units. So the summary is written here
 * instead, from the same option the chart is drawn from, and says what a
 * sighted user sees at a glance:
 *   - time charts: the period, then for each line its newest value, its
 *     lowest and its highest (the min / max of the buckets when there is a
 *     band, so a short spike is not hidden by the averages);
 *   - bar charts: the biggest bars (one series), or the last and the highest
 *     value of each series (several series, like traffic per day);
 *   - pie charts: the biggest slices with their share;
 *   - sankey: the biggest flows;
 *   - scatter (event timeline): how many points.
 * The summary goes in the aria-label of the chart container, with role="img".
 * ------------------------------------------------------------------------- */

/** Most items (slices, bars, flows) and most series named in a summary. */
const SUMMARY_MAX_ITEMS = 5;
const SUMMARY_MAX_SERIES = 8;

/** Puts the summary on the chart container (or removes it when empty). */
function labelChart(container: HTMLElement, summary: string): void {
  if (summary === '') {
    container.removeAttribute('role');
    container.removeAttribute('aria-label');
    return;
  }
  container.setAttribute('role', 'img');
  container.setAttribute('aria-label', summary);
}

/** The number in one data item: a plain number, the value of {value: ...},
 *  or the second item of [time, value, ...]. null when there is none. */
function numberOfItem(item: unknown): number | null {
  if (typeof item === 'number') {
    return item;
  }
  if (Array.isArray(item)) {
    return typeof item[1] === 'number' ? item[1] : null;
  }
  if (item && typeof item === 'object' && 'value' in item) {
    return numberOfItem((item as AnyObj)['value']);
  }
  return null;
}

/** One axis of the option (an option can hold one axis, or a list of them). */
function axisOf(option: EChartsOption, key: 'xAxis' | 'yAxis', index: number): AnyObj | null {
  const axes = (option as AnyObj)[key];
  if (Array.isArray(axes)) {
    return (axes[index] as AnyObj) ?? null;
  }
  return (axes as AnyObj) ?? null;
}

/** The function the chart uses to write its values, in this order: the one
 *  given by the caller, the tooltip's valueFormatter, the label formatter of
 *  the value axis, and fmtNum when there is none. */
function valueFormatterOf(option: EChartsOption, valueAxis: AnyObj | null,
                          given?: Formatter): Formatter {
  if (given) {
    return given;
  }
  const tooltip = (option as AnyObj)['tooltip'] as AnyObj | undefined;
  if (tooltip && typeof tooltip['valueFormatter'] === 'function') {
    return tooltip['valueFormatter'] as Formatter;
  }
  const axisLabel = valueAxis?.['axisLabel'] as AnyObj | undefined;
  if (axisLabel && typeof axisLabel['formatter'] === 'function') {
    return axisLabel['formatter'] as Formatter;
  }
  return fmtNum;
}

/** The summary of a whole chart ('' when there is nothing to say). */
export function describeChart(option: EChartsOption, describeValues?: Formatter): string {
  let seriesList = (option as AnyObj)['series'] as AnyObj[] | AnyObj | undefined;
  if (!seriesList) {
    return '';
  }
  if (!Array.isArray(seriesList)) {
    seriesList = [seriesList];
  }

  // The bands are drawn as two extra series with the line's name: they are
  // not lines of their own (describeLine reads the band from the line).
  const shown: AnyObj[] = [];
  for (const series of seriesList) {
    const id = String(series['id'] ?? '');
    if (!id.startsWith(BAND_ID_PREFIX)) {
      shown.push(series);
    }
  }
  if (!shown.length) {
    return '';
  }

  const firstType = shown[0]['type'];
  if (firstType === 'pie') {
    return describePie(shown[0], describeValues);
  }
  if (firstType === 'sankey') {
    return describeSankey(shown[0], describeValues ?? fmtNum);
  }
  if (firstType === 'scatter') {
    const points = (shown[0]['data'] as unknown[] | undefined)?.length ?? 0;
    return `${shown[0]['name'] ?? 'Points'}: ${points} on the timeline.`;
  }
  if (firstType === 'bar') {
    return describeBars(option, shown, describeValues);
  }
  return describeLines(option, shown, describeValues);
}

/** Time chart: the period, then newest / lowest / highest of each line. */
function describeLines(option: EChartsOption, lines: AnyObj[], describeValues?: Formatter): string {
  let firstTime = Infinity;
  let lastTime = -Infinity;
  const parts: string[] = [];

  for (const line of lines.slice(0, SUMMARY_MAX_SERIES)) {
    const valueAxis = axisOf(option, 'yAxis', Number(line['yAxisIndex'] ?? 0));
    const format = valueFormatterOf(option, valueAxis, describeValues);
    let newest: number | null = null;
    let lowest = Infinity;
    let highest = -Infinity;

    for (const point of (line['data'] as unknown[]) ?? []) {
      // a point is [time, value] or, with a band, [time, average, min, max]
      if (!Array.isArray(point) || typeof point[1] !== 'number') {
        continue;
      }
      const time = point[0] as number;
      const value = point[1];
      firstTime = Math.min(firstTime, time);
      lastTime = Math.max(lastTime, time);
      newest = value;
      let low = value;
      if (typeof point[2] === 'number') {
        low = point[2];
      }
      let high = value;
      if (typeof point[3] === 'number') {
        high = point[3];
      }
      lowest = Math.min(lowest, low);
      highest = Math.max(highest, high);
    }

    const name = String(line['name'] ?? 'Value');
    if (newest === null) {
      parts.push(`${name}: no value.`);
    } else {
      parts.push(`${name}: now ${format(newest)}, lowest ${format(lowest)}, highest ${format(highest)}.`);
    }
  }

  if (lines.length > SUMMARY_MAX_SERIES) {
    parts.push(`And ${lines.length - SUMMARY_MAX_SERIES} more lines.`);
  }
  if (firstTime <= lastTime) {
    parts.unshift(`From ${fmtStamp(firstTime)} to ${fmtStamp(lastTime)}.`);
  }
  return parts.join(' ');
}

/** Bar chart. One series: its biggest bars. Several series (stacked, like
 *  traffic per day): the last and the highest value of each. */
function describeBars(option: EChartsOption, bars: AnyObj[], describeValues?: Formatter): string {
  // the category axis is the one with type 'category' (y for horizontal bars)
  const yAxis = axisOf(option, 'yAxis', 0);
  const horizontal = yAxis?.['type'] === 'category';
  let categoryAxis = axisOf(option, 'xAxis', 0);
  let valueAxis = yAxis;
  if (horizontal) {
    categoryAxis = yAxis;
    valueAxis = axisOf(option, 'xAxis', 0);
  }
  const categories = (categoryAxis?.['data'] as unknown[] | undefined) ?? [];
  const format = valueFormatterOf(option, valueAxis, describeValues);

  if (bars.length === 1) {
    const values = barValues(bars[0]);
    const items: { name: string; value: number }[] = [];
    for (let index = 0; index < values.length; index++) {
      const value = values[index];
      if (value !== null) {
        items.push({ name: String(categories[index] ?? ''), value });
      }
    }
    items.sort(biggestFirst);
    const texts: string[] = [];
    for (const item of items.slice(0, SUMMARY_MAX_ITEMS)) {
      texts.push(`${item.name} ${format(item.value)}`);
    }
    return `${bars[0]['name'] ?? 'Values'}, biggest first: ${texts.join(', ')}.`;
  }

  const parts: string[] = [];
  for (const series of bars.slice(0, SUMMARY_MAX_SERIES)) {
    const values = barValues(series);
    let lastIndex = -1;
    let highestIndex = -1;
    for (let index = 0; index < values.length; index++) {
      const value = values[index];
      if (value === null) {
        continue;
      }
      lastIndex = index;
      if (highestIndex < 0 || value > (values[highestIndex] as number)) {
        highestIndex = index;
      }
    }
    const name = String(series['name'] ?? 'Values');
    if (lastIndex < 0) {
      parts.push(`${name}: no value.`);
      continue;
    }
    parts.push(`${name}: ${categories[lastIndex]} ${format(values[lastIndex])}, `
      + `highest ${categories[highestIndex]} ${format(values[highestIndex])}.`);
  }
  return parts.join(' ');
}

/** The values of a bar series, one per category (null where there is none). */
function barValues(series: AnyObj): (number | null)[] {
  const values: (number | null)[] = [];
  for (const item of (series['data'] as unknown[]) ?? []) {
    values.push(numberOfItem(item));
  }
  return values;
}

/** For sort(): the biggest value first. */
function biggestFirst(first: { value: number }, second: { value: number }): number {
  return second.value - first.value;
}

/** For sort(): the biggest sankey flow first. */
function biggestLinkFirst(first: AnyObj, second: AnyObj): number {
  return Number(second['value'] ?? 0) - Number(first['value'] ?? 0);
}

/** Pie chart: the biggest slices, with their share of the whole. */
function describePie(pie: AnyObj, describeValues?: Formatter): string {
  const slices: { name: string; value: number }[] = [];
  let total = 0;
  for (const item of (pie['data'] as AnyObj[]) ?? []) {
    const value = numberOfItem(item);
    if (value !== null && value > 0) {
      slices.push({ name: String(item['name'] ?? ''), value });
      total += value;
    }
  }
  if (!slices.length) {
    return '';
  }
  slices.sort(biggestFirst);
  const texts: string[] = [];
  for (const slice of slices.slice(0, SUMMARY_MAX_ITEMS)) {
    const share = Math.round((slice.value / total) * 100);
    if (describeValues) {
      texts.push(`${slice.name} ${describeValues(slice.value)} (${share}%)`);
    } else {
      texts.push(`${slice.name} ${share}%`);
    }
  }
  let summary = `${slices.length} parts, biggest first: ${texts.join(', ')}`;
  if (slices.length > SUMMARY_MAX_ITEMS) {
    summary += `, and ${slices.length - SUMMARY_MAX_ITEMS} more`;
  }
  return summary + '.';
}

/** Sankey: the biggest flows. */
function describeSankey(sankey: AnyObj, format: Formatter): string {
  const links = [...((sankey['links'] as AnyObj[]) ?? [])];
  links.sort(biggestLinkFirst);
  const texts: string[] = [];
  for (const link of links.slice(0, SUMMARY_MAX_ITEMS)) {
    texts.push(`${link['source']} to ${link['target']} ${format(Number(link['value'] ?? 0))}`);
  }
  if (!texts.length) {
    return '';
  }
  return `Biggest flows: ${texts.join(', ')}.`;
}

/**
 * My ECharts glue for one component: it owns the chart instances, reads the
 * theme colours, and keeps what the user did to a chart (legend toggles,
 * zoom, graph layout) across the periodic refreshes.
 *
 * Time charts drawn with `linked: true` share one ECharts connect group:
 * zooming one zooms all of them, and the hover cursor follows. For that to
 * line up, every time axis of a render pass uses the same extent
 * (beginPass computes it from all the time bases of the document).
 */
export class ChartKit {
  /** series palette, read again from the CSS variables on every pass */
  pal: string[] = FALLBACK_PALETTE;
  /** shared [min, max] of the time axes drawn in this pass */
  timeExtent: [number, number] | null = null;
  readonly group = 'fortinet-time-' + (++kitSequence);

  private charts: Record<string, echarts.ECharts> = {};
  private bandSeq = 0;
  private forget: 'none' | 'zoom' | 'all' = 'none';

  constructor(private host: HTMLElement, private zone: NgZone,
              private bandOpacity = 0.14) {
    echarts.connect(this.group);
  }

  /** Starts drawing a document: theme colours, shared time extent, and
   *  disposes the charts whose container left the page. */
  beginPass(timeBases: (number[] | null | undefined)[]): void {
    const palette: string[] = [];
    for (let colorIndex = 0; colorIndex < FALLBACK_PALETTE.length; colorIndex++) {
      const fromCss = this.cssVar(`--highcharts-color-${colorIndex}`);
      palette.push(fromCss || FALLBACK_PALETTE[colorIndex]);
    }
    this.pal = palette;
    this.bandSeq = 0;

    // earliest and latest time over all the time bases
    let lowest = Infinity;
    let highest = -Infinity;
    for (const times of timeBases) {
      if (times && times.length) {
        lowest = Math.min(lowest, times[0]);
        highest = Math.max(highest, times[times.length - 1]);
      }
    }
    this.timeExtent = lowest < highest ? [lowest, highest] : null;
    this.prune();
  }

  /** The pass is over: a pending forgetState has been applied. */
  endPass(): void {
    this.forget = 'none';
  }

  /** Forget what the user did to the charts at the next pass: 'zoom' after
   *  a history window change, 'all' after switching device. */
  forgetState(level: 'zoom' | 'all'): void {
    // 'all' always wins, 'zoom' only if nothing is pending yet
    if (level === 'all' || this.forget === 'none') {
      this.forget = level;
    }
  }

  el(id: string): HTMLElement | null {
    return this.host.querySelector<HTMLElement>('#' + id);
  }

  /** The instance for a container, or null when the container is not in
   *  the page (its card or tab is hidden). A container recreated by
   *  Angular gets a new instance. */
  private chart(id: string): echarts.ECharts | null {
    const element = this.el(id);
    const existing = this.charts[id];
    if (existing && existing.getDom() === element) {
      return existing;
    }
    if (existing) {
      existing.dispose();
      delete this.charts[id];
    }
    if (!element) {
      return null;
    }
    element.innerHTML = ''; // clears a previous markEmpty message
    // Outside Angular: the ECharts mouse listeners must not trigger change
    // detection for the whole page.
    const created = this.zone.runOutsideAngular(() => echarts.init(element));
    this.charts[id] = created;
    return created;
  }

  /** Draws `option` into the container `id`, skipped when it's hidden.
   *
   *  linked        time chart: joins the zoom / cursor group
   *  stableLayout  force-directed graph: merge instead of replace, so the
   *                nodes keep their positions between refreshes */
  setChart(id: string, option: EChartsOption,
           options: { linked?: boolean; stableLayout?: boolean; describeValues?: Formatter } = {}): void {
    const chart = this.chart(id);
    if (!chart) return;
    const forget = this.forget;
    // What a screen reader says for the chart (a canvas has no text):
    // a short summary of what it shows. See describeChart.
    labelChart(chart.getDom(), describeChart(option, options.describeValues));

    this.zone.runOutsideAngular(() => {
      if (options.stableLayout) {
        if (forget === 'all') {
          chart.clear();
        }
        chart.setOption(option, { notMerge: false });
        return;
      }

      const previous = (chart.getOption() ?? {}) as PrevOption;

      // Keep the legend toggles the user made (unless I forget everything)
      let selected: Record<string, boolean> | undefined;
      if (forget !== 'all') {
        selected = previous.legend?.[0]?.selected;
      }
      const legend = option.legend;
      const legendIsShown = legend && !Array.isArray(legend)
        && (legend as AnyObj)['show'] !== false;
      if (selected && legendIsShown) {
        option = { ...option, legend: { ...legend, selected: { ...selected } } };
      }

      // Keep the zoom too, unless I was asked to forget it
      let zoom: SavedZoom | undefined;
      if (forget === 'none') {
        zoom = previous.dataZoom?.find((savedZoom) => savedZoom.id === TIME_ZOOM_ID);
      }

      chart.setOption(option, true);
      if (options.linked) {
        (chart as unknown as { group: string }).group = this.group;
      }
      if (zoom && isZoomed(zoom)) {
        // silent: putting back this chart's own view must not be echoed to
        // the linked charts, they put back their own view themselves
        chart.dispatchAction({
          type: 'dataZoom', dataZoomId: TIME_ZOOM_ID,
          start: zoom.start, end: zoom.end,
          startValue: zoom.startValue, endValue: zoom.endValue,
        }, { silent: true });
      }
    });
  }

  markEmpty(id: string, message: string): void {
    const element = this.el(id);
    if (this.charts[id]) {
      this.charts[id].dispose();
      delete this.charts[id];
    }
    if (element) {
      // the message is real text now: the summary of the old chart goes
      element.removeAttribute('role');
      element.removeAttribute('aria-label');
      element.innerHTML = '<div class="empty">' + esc(message) + '</div>';
    }
  }

  /** Disposes every chart whose container left the page. */
  prune(): void {
    for (const id of Object.keys(this.charts)) {
      if (this.charts[id].getDom() !== this.el(id)) {
        this.charts[id].dispose();
        delete this.charts[id];
      }
    }
  }

  resize(): void {
    for (const chart of Object.values(this.charts)) {
      chart.resize();
    }
  }

  /** Calls onWidthChange each time the page element changes WIDTH, whatever
   *  the reason: the window was resized, but also the menu of the NetScope
   *  shell was collapsed or opened (the window keeps its size then, so a
   *  "window resize" listener never hears of it, and the charts kept their
   *  old width and went out of their cards). Height changes are ignored:
   *  the page gets taller or shorter at every refresh.
   *  Returns the function that stops watching. */
  watchWidth(element: HTMLElement, onWidthChange: () => void): () => void {
    let lastWidth = element.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = element.clientWidth;
      if (width !== lastWidth) {
        lastWidth = width;
        onWidthChange();
      }
    });
    // outside Angular: nothing on screen depends on it but the charts
    this.zone.runOutsideAngular(() => observer.observe(element));
    return () => observer.disconnect();
  }

  /** Resizes one chart after its container changed height (does nothing
   *  when the size didn't change). */
  resizeChart(id: string): void {
    const chart = this.charts[id];
    if (chart && chart.getDom() === this.el(id)) {
      this.zone.runOutsideAngular(() => chart.resize());
    }
  }

  dispose(): void {
    for (const chart of Object.values(this.charts)) {
      chart.dispose();
    }
    this.charts = {};
    echarts.disconnect(this.group);
  }

  cssVar(name: string): string {
    return getComputedStyle(this.host).getPropertyValue(name).trim();
  }

  /** --highcharts-neutral-color-<level>, with a fixed fallback. */
  neutral(level: number, fallback: string): string {
    return this.cssVar(`--highcharts-neutral-color-${level}`) || fallback;
  }

  /** Colour of the axis lines and of the borders between pie slices: the
   *  --clr-axis token (from --app-axis of the app theme, see styles.css).
   *  It used to be --highcharts-neutral-color-20 in both themes, which is
   *  #333333 in the dark theme: hardly visible on the dark cards. */
  axisLineColor(): string {
    return this.cssVar('--clr-axis') || this.neutral(20, '#cccccc');
  }

  /** Colours of a tooltip box, applied to every chart by base(). */
  tooltipStyle(): AnyObj {
    return {
      backgroundColor: this.cssVar('--clr-card') || '#ffffff',
      borderColor: this.cssVar('--clr-border'),
      textStyle: { color: this.cssVar('--clr-ink'), fontSize: 12 },
      extraCssText: 'box-shadow:0 1px 3px rgba(0,0,0,.15);',
    };
  }

  /** The common option. A chart's own `tooltip` is merged over the themed
   *  tooltip style instead of replacing it, so every tooltip follows the
   *  light / dark theme. */
  base(extra: EChartsOption): EChartsOption {
    const ownTooltip = extra.tooltip;
    let tooltip: unknown;
    if (Array.isArray(ownTooltip)) {
      tooltip = ownTooltip;
    } else {
      tooltip = { trigger: 'axis', ...this.tooltipStyle(), ...(ownTooltip || {}) };
    }
    return {
      backgroundColor: 'transparent',
      textStyle: { color: this.cssVar('--clr-ink'), fontSize: 12, fontFamily: FONT },
      grid: { ...DEFAULT_GRID },
      legend: {
        textStyle: { color: this.cssVar('--clr-ink-dim'), fontSize: 11 },
        bottom: 0, type: 'scroll', itemWidth: 18, itemHeight: 9,
        pageIconColor: this.cssVar('--clr-blue'),
        pageTextStyle: { color: this.cssVar('--clr-ink-dim') },
      },
      color: this.pal,
      ...extra,
      tooltip: tooltip as EChartsOption['tooltip'],
    } as EChartsOption;
  }

  /** A grid with room at the top for the zoom tool, for a chart that has
   *  an axis on the RIGHT. The zoom tool (timeZoom) sits in the top right
   *  corner, which is exactly where the highest label of a right axis
   *  ("100 %") is drawn with the default grid: the icons and the label
   *  overlapped, and the icons were hard to click. Charts with a left axis
   *  only don't need it.
   *  grid: the chart's own grid (its `top` is replaced), the default one
   *  when not given. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridUnderZoomTool(grid: { [key: string]: any } = DEFAULT_GRID): { [key: string]: any } {
    return { ...grid, top: ZOOM_TOOL_GRID_TOP };
  }

  /** Zoom for a time chart: Ctrl + mouse wheel, dragging once zoomed, and a
   *  zoom tool in the corner (select a range, undo, reset). */
  timeZoom(xAxisIndex: number | number[] | 'all' = 0): EChartsOption {
    const dimColor = this.cssVar('--clr-ink-dim') || '#737373';
    const blueColor = this.cssVar('--clr-blue') || '#0072a3';
    return {
      dataZoom: [{
        type: 'inside', id: TIME_ZOOM_ID, xAxisIndex,
        filterMode: 'none', zoomOnMouseWheel: 'ctrl',
        moveOnMouseWheel: false, moveOnMouseMove: true,
      }],
      toolbox: {
        right: 4, top: 0, itemSize: 13, itemGap: 8,
        iconStyle: { borderColor: dimColor },
        emphasis: {
          iconStyle: { borderColor: blueColor, textFill: this.cssVar('--clr-ink-strong') },
        },
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: { zoom: 'Zoom into a time range', back: 'Undo zoom' },
          },
          restore: { title: 'Reset zoom' },
        },
      },
    } as EChartsOption;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  timeAxis(): any {
    const axis: AnyObj = { type: 'time' };
    // every time chart of the pass gets the same extent, so they line up
    if (this.timeExtent) {
      axis['min'] = this.timeExtent[0];
      axis['max'] = this.timeExtent[1];
    }
    axis['axisLine'] = { lineStyle: { color: this.axisLineColor() } };
    axis['axisLabel'] = { color: this.cssVar('--clr-ink-dim'), hideOverlap: true };
    axis['splitLine'] = { show: false };
    return axis;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valAxis(formatter?: Formatter): any {
    return {
      type: 'value',
      axisLabel: {
        color: this.cssVar('--clr-ink-dim'),
        formatter: (formatter || fmtNum) as never,
      },
      splitLine: { lineStyle: { color: this.neutral(10, '#ececec') } },
    };
  }

  /** Dashed horizontal lines at the warning and danger levels of a
   *  threshold, for the `markLine` option of a line series (they follow the
   *  axes and the legend toggle of that series). These are the levels the
   *  backend raises issues from, so the lines show where an issue starts. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  thresholdMarks(level: Level, formatter: Formatter = (value) => String(value),
                 markOptions: ThresholdMarkOptions = {}): any {
    const at = markOptions.at ?? 'end';

    const makeLine = (value: number, sev: Sev) => {
      let color: string;
      let what: string;
      if (sev === 'danger') {
        color = this.cssVar('--clr-danger') || '#c21d00';
        what = 'critical';
      } else {
        color = this.cssVar('--clr-warning') || '#a36500';
        what = 'warning';
      }
      // e.g. "insideEndTop": which end of the line, then above or below it
      const horizontal = at === 'start' ? 'insideStart' : 'insideEnd';
      const vertical = markOptions.below ? 'Bottom' : 'Top';
      const prefix = markOptions.name ? markOptions.name + ' ' : '';
      return {
        yAxis: value,
        lineStyle: {
          color,
          type: markOptions.dotted ? 'dotted' : 'dashed',
          width: 1,
          opacity: 0.8,
        },
        label: {
          color,
          fontSize: 10,
          position: horizontal + vertical,
          formatter: `${prefix}${what} ${formatter(value)}`,
        },
      };
    };

    const lines = [makeLine(level.warning, 'warning')];
    if (level.danger != null) {
      lines.push(makeLine(level.danger, 'danger'));
    }
    return { silent: true, symbol: 'none', animation: false, data: lines };
  }

  /** The name of a value axis written vertically along it, at mid-height,
   *  instead of above its top end: that way the top corners stay free for
   *  the zoom buttons. Needs a grid without containLabel, with room for
   *  `gap` px (tick labels plus the name) on that side. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sideAxisName(name: string, side: 'left' | 'right', gap = 44): any {
    return {
      name, nameLocation: 'middle', nameGap: gap,
      nameRotate: side === 'left' ? 90 : -90,
      nameTextStyle: { color: this.cssVar('--clr-ink-dim'), fontSize: 11 },
    };
  }

  /** Axis tooltip for the charts built with lineWithBand: one row per line,
   *  without the band helpers, and the min and max of a bucket shown after
   *  its average. `perSeries` replaces `formatter` by series name (for
   *  charts that mix units). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bandTooltip(formatter: Formatter, perSeries?: Record<string, Formatter>): any {
    type TooltipParam = { seriesId?: string; seriesName?: string; marker?: string;
                          value?: unknown; axisValueLabel?: string };
    return {
      trigger: 'axis',
      formatter: (raw: TooltipParam | TooltipParam[]) => {
        const params = Array.isArray(raw) ? raw : [raw];
        let rows = '';
        for (const param of params) {
          // the band helper series are not real lines, skip them
          if (String(param.seriesId ?? '').startsWith(BAND_ID_PREFIX)) {
            continue;
          }
          const point = (Array.isArray(param.value) ? param.value : []) as (number | null)[];
          // a point is [time, value, min, max]
          const value = point[1];
          const low = point[2];
          const high = point[3];
          if (value == null) {
            continue; // gap marker or missing sample
          }
          const format = perSeries?.[param.seriesName ?? ''] ?? formatter;
          let range = '';
          if (low != null && high != null && low !== high) {
            range = ` <span style="opacity:.7">(min ${format(low)}, max ${format(high)})</span>`;
          }
          rows += `<br>${param.marker ?? ''}${esc(param.seriesName ?? '')}: `
            + `<b>${format(value)}</b>${range}`;
        }
        return esc(params[0]?.axisValueLabel ?? '') + rows;
      },
    };
  }

  /** A line plus, when `band` has min and max, the shaded band between them.
   *  The band is two stacked series with invisible lines: the lower edge,
   *  then (max - min) on top of it with an area fill. All three share the
   *  line's name, so the legend toggles them together. */
  lineWithBand(name: string, times: number[], values: Values, band?: Band,
               // eslint-disable-next-line @typescript-eslint/no-explicit-any
               seriesOptions?: { [key: string]: any }): SeriesOption[] {
    const line = this.lineSeries(name, times, values, seriesOptions, band);
    const lows = band?.min;
    const highs = band?.max;
    if (!lows || !highs) {
      return [line];
    }

    const stack = BAND_ID_PREFIX + (this.bandSeq++);

    // the height of the band at each point
    const spread: Values = [];
    for (let index = 0; index < highs.length; index++) {
      const high = highs[index];
      const low = lows[index];
      if (high == null || low == null) {
        spread.push(null);
      } else {
        spread.push(high - low);
      }
    }

    const shared: AnyObj = {
      stack,
      smooth: false,
      silent: true,
      z: 1,
      lineStyle: { width: 0, opacity: 0 },
      emphasis: { disabled: true },
    };
    // the band follows the same colour and axes as its line
    for (const key of ['color', 'xAxisIndex', 'yAxisIndex']) {
      if (seriesOptions?.[key] !== undefined) {
        shared[key] = seriesOptions[key];
      }
    }

    const lowerEdge = Object.assign(this.lineSeries(name, times, lows), shared,
                                    { id: stack + ':min' }) as SeriesOption;
    const filledPart = Object.assign(this.lineSeries(name, times, spread), shared,
                                     { id: stack + ':max',
                                       areaStyle: { opacity: this.bandOpacity } }) as SeriesOption;
    return [line, lowerEdge, filledPart];
  }

  /** One line series. With a band, each point also carries the min and max
   *  of its bucket ([time, avg, min, max]) for the tooltip. */
  private lineSeries(name: string, times: number[], values: Values,
                     seriesOptions?: object, band?: Band): SeriesOption {
    // The time between each sample and the next one, to spot outages:
    // deltas[0] is between times[0] and times[1], and so on.
    const deltas: number[] = [];
    for (let index = 1; index < times.length; index++) {
      deltas.push(times[index] - times[index - 1]);
    }

    const lows = band?.min;
    const highs = band?.max;
    const withBand = !!(lows && highs);

    const data: (number | null)[][] = [];
    for (let index = 0; index < times.length; index++) {
      // A hole much bigger than usual around it: I add a null point in the
      // middle so ECharts breaks the line instead of drawing across the
      // outage. The hole before times[index] is deltas[index - 1].
      if (index > 0 && isOutage(deltas, index - 1)) {
        const middle = times[index - 1] + (times[index] - times[index - 1]) / 2;
        if (withBand) {
          data.push([middle, null, null, null]);
        } else {
          data.push([middle, null]);
        }
      }
      if (withBand) {
        data.push([times[index], values[index] ?? null, lows![index] ?? null, highs![index] ?? null]);
      } else {
        data.push([times[index], values[index] ?? null]);
      }
    }

    const series: AnyObj = {
      name, type: 'line', showSymbol: false, smooth: 0.15,
      connectNulls: false, data,
    };
    if (withBand) {
      // tells ECharts to draw column 1 (the average), not the min or max
      series['encode'] = { x: 0, y: 1 };
    }
    return Object.assign(series, seriesOptions || {}) as SeriesOption;
  }
}

/** A view narrower than the whole axis. ECharts gives the percentages even
 *  for a zoom set by values, and gives values even when nothing is zoomed,
 *  so the percentages decide. */
function isZoomed(zoom: SavedZoom): boolean {
  if (zoom.start != null || zoom.end != null) {
    return (zoom.start ?? 0) > 0.01 || (zoom.end ?? 100) < 99.99;
  }
  return zoom.startValue != null || zoom.endValue != null;
}

/** SVG path of a small trend line (sparkline) in a width x height box.
 *  A null breaks the line. '' when there's nothing to draw. */
export function sparkPath(values: Values, width: number, height: number): string {
  const numbers: number[] = [];
  for (const value of values) {
    if (value != null) {
      numbers.push(value);
    }
  }
  if (numbers.length < 2) return '';

  const lowest = Math.min(...numbers);
  const highest = Math.max(...numbers);
  // a flat line would divide by zero, so I use 1 instead
  const span = highest - lowest || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;

  let path = '';
  // penDown: false after a gap, so the next point starts a new piece (M)
  let penDown = false;
  for (let index = 0; index < values.length; index++) {
    const value = values[index];
    if (value == null) {
      penDown = false;
      continue;
    }
    const pointX = (index * step).toFixed(1);
    // SVG y goes down, so the biggest value is near the top (1 px margin)
    const pointY = (height - 1 - ((value - lowest) / span) * (height - 2)).toFixed(1);
    path += (penDown ? 'L' : 'M') + pointX + ' ' + pointY;
    penDown = true;
  }
  return path;
}

/* ============================================================================
 * Anonymised export
 * ========================================================================== */

/** A value to hide, with the kind of placeholder it gets ("DEVICE",
 *  "USER", ...). */
export interface Secret {
  kind: string;
  value: string | null | undefined;
}

/** Escapes the characters that mean something in a regular expression. */
function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const IPV4 = /\b(?:\d{1,3}\.){3}\d{1,3}(?:\/\d{1,2})?\b/g;
const MAC = /\b(?:[0-9a-f]{2}[:-]){5}[0-9a-f]{2}\b/gi;
const IPV6 = /(?:[0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}/gi;
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const SERIAL = /\bF[A-Z0-9]{2,5}[A-Z0-9]{8,14}\b/g;
const FQDN = /\b[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+\.[a-z]{2,24}\b/gi;

/** A match of IPV6 that really is an address: "10:42" or "a:b" is not. */
function looksLikeIpv6(match: string): boolean {
  if (match.includes('::')) {
    return /[0-9a-f]/i.test(match);
  }
  const colons = match.match(/:/g) || [];
  return colons.length >= 4;
}

/** A match of SERIAL that really is a serial: it has digits in it. */
function looksLikeSerial(match: string): boolean {
  const digits = match.match(/\d/g) || [];
  return digits.length >= 4;
}

/**
 * Replaces the identifying values in a text with stable placeholders
 * (DEVICE-1, IP-2, USER-1, ...): first the secrets I give it, then anything
 * that looks like an IP or MAC address, a FortiGate serial, an e-mail
 * address or a host name. The same value always gets the same placeholder,
 * so the text stays readable ("IP-3 is down on DEVICE-1").
 *
 * leaks() then checks the result: if it returns something, a value was
 * missed and the text must not be shared.
 */
export class Anonymizer {
  private tokens = new Map<string, string>();
  private counts = new Map<string, number>();
  private literals: string[] = [];

  constructor(secrets: Secret[]) {
    for (const secret of secrets) {
      const value = (secret.value ?? '').trim();
      // very short values ("a", "1") would mangle normal words
      if (value.length < 3 || this.tokens.has(value)) {
        continue;
      }
      this.tokenFor(secret.kind, value);
      this.literals.push(value);
    }
    // longest first, so "fw1-backup" is replaced before "fw1"
    this.literals.sort((first, second) => second.length - first.length);
  }

  /** How many different values were replaced so far. */
  get replaced(): number {
    return this.tokens.size;
  }

  private tokenFor(kind: string, value: string): string {
    let token = this.tokens.get(value);
    if (!token) {
      const number = (this.counts.get(kind) ?? 0) + 1;
      this.counts.set(kind, number);
      token = `${kind}-${number}`;
      this.tokens.set(value, token);
    }
    return token;
  }

  /** A literal as a whole word: "alice" doesn't match inside "malice", and
   *  "fw1" doesn't match inside "fw1.example.com". */
  private static wordRe(literal: string): RegExp {
    // before: start of text or a character that can't be part of a name
    // after: not followed by a name character, or a dot and more name
    return new RegExp(`(^|[^\\w.-])${escapeRe(literal)}(?![\\w-]|\\.\\w)`, 'g');
  }

  scrub(text: string): string {
    for (const literal of this.literals) {
      // "before" is the character caught in front of the word, I put it back
      text = text.replace(Anonymizer.wordRe(literal),
        (_match, before: string) => before + this.tokens.get(literal));
    }
    // The order matters: e-mails before host names, MAC before IPv6, etc.
    text = text.replace(EMAIL, (match) => this.tokenFor('EMAIL', match));
    text = text.replace(MAC, (match) => this.tokenFor('MAC', match.toLowerCase()));
    text = text.replace(IPV6, (match) => {
      if (!looksLikeIpv6(match)) return match;
      return this.tokenFor('IP', match.toLowerCase());
    });
    text = text.replace(IPV4, (match) => this.tokenFor('IP', match));
    text = text.replace(SERIAL, (match) => {
      if (!looksLikeSerial(match)) return match;
      return this.tokenFor('SERIAL', match);
    });
    text = text.replace(FQDN, (match) => this.tokenFor('HOST', match.toLowerCase()));
    return text;
  }

  /** What still looks identifying in `text` (empty when it's clean). */
  leaks(text: string): string[] {
    const found = new Set<string>();
    for (const literal of this.literals) {
      if (Anonymizer.wordRe(literal).test(text)) {
        // "DEVICE-1" -> "device"
        const kind = this.tokens.get(literal)!.replace(/-\d+$/, '').toLowerCase();
        found.add(`a known ${kind} value`);
      }
    }
    if ((text.match(IPV4) || []).length) {
      found.add('an IPv4 address');
    }
    if ((text.match(IPV6) || []).some(looksLikeIpv6)) {
      found.add('an IPv6 address');
    }
    if ((text.match(MAC) || []).length) {
      found.add('a MAC address');
    }
    if ((text.match(EMAIL) || []).length) {
      found.add('an e-mail address');
    }
    if ((text.match(SERIAL) || []).some(looksLikeSerial)) {
      found.add('a serial number');
    }
    return [...found];
  }
}


/* ============================================================================
 * Shell navigation
 *
 * The Fortinet pages don't draw their own device picker and tab strip
 * anymore: FortinetNavComponent (fortinet-nav.component.ts) draws them in
 * the vertical navigation of the application shell (dcnetscope-main.component).
 * They talk through this service.
 *
 *   FortinetDashboardComponent  publishes what the navigation shows
 *                               (patch) and does what the user picks
 *                               there (commands$)
 *   FortinetNavComponent        reads state$ and sends commands (send)
 *   DcnetscopeMainComponent     reads state$.active to show this menu
 *                               instead of its own, and sends the top
 *                               bar's period (sendTimeSelection)
 *
 * The service is provided in root, so it survives a navigation between the
 * flow pages and the Fortinet page. The dashboard calls clear() when it's
 * destroyed, which brings the flow menu back.
 * ========================================================================== */

/** Picker value of the "All devices" entry. It's not a valid FortiGate
 *  serial, so it can never collide with a real device. */
export const FORTINET_FLEET_VIEW = '__all__';

/** Colour of the dot next to a device in the picker. */
export type NavSev = 'ok' | 'warning' | 'danger' | 'offline' | 'unknown';

/** A device as the picker lists it. */
export interface NavDevice {
  serial: string;
  /** what the picker shows, e.g. "bureau-fw (FGT30E3U17039682)" */
  text: string;
  label: string;
  /** every group of the device, main group first ([] without any) */
  groups: string[];
  sev: NavSev;
}

/** A group of devices (config.yaml `groups:`), as the fleet page counts them. */
export interface NavGroup {
  /** the full path ("France/Paris"), '' for the devices without a group */
  name: string;
  /** the last level ("Paris"), or "No group" */
  label: string;
  /** 0 for a top group, 1 for a group inside it... (to indent it) */
  depth: number;
  /** devices in this group or in a group below it */
  devices: number;
}

/** One entry of the navigation: a device tab, or a section of the fleet page. */
export interface NavEntry {
  id: string;
  label: string;
  /** cds-icon shape; I have to register it in the shell before using a new one */
  icon: string;
  /** a count shown on the right, e.g. the open issues */
  badge?: number | null;
  /** true to draw the badge in the danger colour */
  badgeBad?: boolean;
  /** why the entry can't be opened, or '' */
  off?: string;
}

/** Everything the shell needs to draw the Fortinet navigation. */
export interface NavState {
  /** a Fortinet page is mounted: the shell shows this menu */
  active: boolean;
  /** the all-devices page is shown */
  fleetMode: boolean;
  devices: NavDevice[];
  groups: NavGroup[];
  /** '' for the devices without a group, null for every group */
  groupFilter: string | null;
  selectedSerial: string | null;
  /** title of the selected device, as the switcher shows it */
  deviceLabel: string;
  /** second line of the switcher: main group and address */
  deviceSub: string;
  deviceSev: NavSev;
  /** id of the entry shown (a DeviceTab, or a fleet section) */
  current: string;
  /** device tabs, when a device is selected */
  sections: NavEntry[];
  /** sections of the all-devices page */
  fleetSections: NavEntry[];
}

export const NAV_EMPTY: NavState = {
  active: false,
  fleetMode: false,
  devices: [],
  groups: [],
  groupFilter: null,
  selectedSerial: null,
  deviceLabel: '',
  deviceSub: '',
  deviceSev: 'unknown',
  current: 'overview',
  sections: [],
  fleetSections: [],
};

/** What the shell asks the Fortinet page to do. */
export type NavCommand =
  | { kind: 'device'; serial: string }      // FORTINET_FLEET_VIEW for all devices
  | { kind: 'tab'; tab: DeviceTab }
  | { kind: 'section'; id: string }         // fleet page: scroll to a section
  | { kind: 'group'; name: string | null }  // fleet page: focus a group (a full path)
  /** The application's own time selector changed, or the user just arrived
   *  on the Fortinet page (arrival: true): a HISTORY_RANGES id of
   *  dashboard.py for the History window to follow. */
  | { kind: 'range'; id: string; arrival: boolean };

/** The time selector of the top bar, as HISTORY_RANGES ids of the
 *  Fortinet documents (see dashboard.py). The Fortinet page has one more window
 *  ("1h") that the bar does not offer. */
const FORTINET_RANGE_OF_SELECTION: Record<string, string> = {
  'last 1day': '24h',
  'last 1week': '7d',
  'last 1month': '1mo',
  'last 3months': '3mo',
  'last 6months': '6mo',
  'last 1year': '1y',
};

/** Length of each Fortinet window, in seconds (dashboard.HISTORY_RANGES),
 *  shortest first: a custom period picks the shortest one covering it. */
const FORTINET_RANGE_SECONDS: [string, number][] = [
  ['1h', 3600],
  ['24h', 24 * 3600],
  ['7d', 7 * 86400],
  ['1mo', 30 * 86400],
  ['3mo', 91 * 86400],
  ['6mo', 182 * 86400],
  ['1y', 365 * 86400],
];

/** The top bar's period as a Fortinet window id, or undefined when there is
 *  none (a custom period that has not started yet).
 *  requestedTimeSelection: "last 1day", ..., or "custom"
 *  startTime:              start of a custom period, in epoch seconds */
export function fortinetRangeOfSelection(requestedTimeSelection: string,
                                         startTime: number): string | undefined {
  if (requestedTimeSelection !== 'custom') {
    return FORTINET_RANGE_OF_SELECTION[requestedTimeSelection];
  }
  // The Fortinet windows all end now: take the shortest one reaching
  // back to the start of the custom period.
  const span = Date.now() / 1000 - startTime;
  if (!startTime || span <= 0) {
    return undefined;
  }
  for (const [rangeId, seconds] of FORTINET_RANGE_SECONDS) {
    // 2 % of slack, so a period a bit longer than a window still fits in it
    if (span <= seconds * 1.02) {
      return rangeId;
    }
  }
  // longer than every window: the longest one
  return FORTINET_RANGE_SECONDS[FORTINET_RANGE_SECONDS.length - 1][0];
}

@Injectable({ providedIn: 'root' })
export class FortinetNavService {
  private readonly state = new BehaviorSubject<NavState>(NAV_EMPTY);
  private readonly commands = new Subject<NavCommand>();

  /** What the shell draws. */
  readonly state$: Observable<NavState> = this.state.asObservable();
  /** What the Fortinet page does. */
  readonly commands$: Observable<NavCommand> = this.commands.asObservable();

  get snapshot(): NavState {
    return this.state.value;
  }

  /** Publishes a change (called by the Fortinet page). */
  patch(next: Partial<NavState>): void {
    this.state.next({ ...this.state.value, ...next });
  }

  /** Leaving the Fortinet page: the flow menu comes back. */
  clear(): void {
    this.state.next(NAV_EMPTY);
  }

  /** Asks the Fortinet page to do something (called by the shell). */
  send(command: NavCommand): void {
    this.commands.next(command);
  }

  /** Tells the Fortinet page, when one is open, which window the top bar
   *  asks for (called by the shell). arrival: the user just opened the page. */
  sendTimeSelection(requestedTimeSelection: string, startTime: number, arrival: boolean): void {
    if (!this.state.value.active) {
      return;
    }
    const rangeId = fortinetRangeOfSelection(requestedTimeSelection, startTime);
    if (rangeId) {
      this.send({ kind: 'range', id: rangeId, arrival });
    }
  }
}
